/**
 * Translation Service
 * Uses our translate-content Edge Function (Gemini, key on the server) only —
 * no third-party fallback: private messages never leave our own backend + Gemini.
 * Supports 95+ languages from Chat Translator
 */

export interface LanguageInfo {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
  priority: number; // 1 = highest priority
}

// All supported languages (sorted by priority for Croatian vacation rentals)
export const WORLD_LANGUAGES: LanguageInfo[] = [
  // Priority 1: English + Croatian (app languages)
  { code: 'en', name: 'English', nativeName: 'English', flag: '🇬🇧', priority: 1 },
  { code: 'hr', name: 'Croatian', nativeName: 'Hrvatski', flag: '🇭🇷', priority: 1 },

  // Priority 2: Top European tourist languages (most common visitors to Croatia)
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '🇩🇪', priority: 2 },
  { code: 'it', name: 'Italian', nativeName: 'Italiano', flag: '🇮🇹', priority: 2 },
  { code: 'sl', name: 'Slovenian', nativeName: 'Slovenščina', flag: '🇸🇮', priority: 2 },
  { code: 'cs', name: 'Czech', nativeName: 'Čeština', flag: '🇨🇿', priority: 2 },
  { code: 'pl', name: 'Polish', nativeName: 'Polski', flag: '🇵🇱', priority: 2 },
  { code: 'hu', name: 'Hungarian', nativeName: 'Magyar', flag: '🇭🇺', priority: 2 },
  { code: 'sk', name: 'Slovak', nativeName: 'Slovenčina', flag: '🇸🇰', priority: 2 },
  { code: 'fr', name: 'French', nativeName: 'Français', flag: '🇫🇷', priority: 2 },
  { code: 'nl', name: 'Dutch', nativeName: 'Nederlands', flag: '🇳🇱', priority: 2 },
  { code: 'es', name: 'Spanish', nativeName: 'Español', flag: '🇪🇸', priority: 2 },

  // Priority 3: Other European languages
  { code: 'pt', name: 'Portuguese', nativeName: 'Português', flag: '🇵🇹', priority: 3 },
  { code: 'ru', name: 'Russian', nativeName: 'Русский', flag: '🇷🇺', priority: 3 },
  { code: 'uk', name: 'Ukrainian', nativeName: 'Українська', flag: '🇺🇦', priority: 3 },
  { code: 'sr', name: 'Serbian', nativeName: 'Српски', flag: '🇷🇸', priority: 3 },
  { code: 'bs', name: 'Bosnian', nativeName: 'Bosanski', flag: '🇧🇦', priority: 3 },
  { code: 'mk', name: 'Macedonian', nativeName: 'Македонски', flag: '🇲🇰', priority: 3 },
  { code: 'bg', name: 'Bulgarian', nativeName: 'Български', flag: '🇧🇬', priority: 3 },
  { code: 'ro', name: 'Romanian', nativeName: 'Română', flag: '🇷🇴', priority: 3 },
  { code: 'el', name: 'Greek', nativeName: 'Ελληνικά', flag: '🇬🇷', priority: 3 },
  { code: 'fi', name: 'Finnish', nativeName: 'Suomi', flag: '🇫🇮', priority: 3 },
  { code: 'sv', name: 'Swedish', nativeName: 'Svenska', flag: '🇸🇪', priority: 3 },
  { code: 'no', name: 'Norwegian', nativeName: 'Norsk', flag: '🇳🇴', priority: 3 },
  { code: 'da', name: 'Danish', nativeName: 'Dansk', flag: '🇩🇰', priority: 3 },
  { code: 'is', name: 'Icelandic', nativeName: 'Íslenska', flag: '🇮🇸', priority: 3 },
  { code: 'et', name: 'Estonian', nativeName: 'Eesti', flag: '🇪🇪', priority: 3 },
  { code: 'lv', name: 'Latvian', nativeName: 'Latviešu', flag: '🇱🇻', priority: 3 },
  { code: 'lt', name: 'Lithuanian', nativeName: 'Lietuvių', flag: '🇱🇹', priority: 3 },
  { code: 'mt', name: 'Maltese', nativeName: 'Malti', flag: '🇲🇹', priority: 3 },
  { code: 'ga', name: 'Irish', nativeName: 'Gaeilge', flag: '🇮🇪', priority: 3 },
  { code: 'cy', name: 'Welsh', nativeName: 'Cymraeg', flag: '🏴󠁧󠁢󠁷󠁬󠁳󠁿', priority: 3 },
  { code: 'eu', name: 'Basque', nativeName: 'Euskera', flag: '🏴󠁥󠁳󠁰󠁶󠁿', priority: 3 },
  { code: 'ca', name: 'Catalan', nativeName: 'Català', flag: '🏴󠁥󠁳󠁣󠁴󠁿', priority: 3 },
  { code: 'gl', name: 'Galician', nativeName: 'Galego', flag: '🏴󠁥󠁳󠁧󠁡󠁿', priority: 3 },
  { code: 'be', name: 'Belarusian', nativeName: 'Беларуская', flag: '🇧🇾', priority: 3 },
  { code: 'sq', name: 'Albanian', nativeName: 'Shqip', flag: '🇦🇱', priority: 3 },
  { code: 'tr', name: 'Turkish', nativeName: 'Türkçe', flag: '🇹🇷', priority: 3 },

  // Priority 4: Wealthy nations / common tourists from far
  { code: 'zh', name: 'Chinese (Simplified)', nativeName: '中文 (简体)', flag: '🇨🇳', priority: 4 },
  { code: 'zh-TW', name: 'Chinese (Traditional)', nativeName: '中文 (繁體)', flag: '🇹🇼', priority: 4 },
  { code: 'ja', name: 'Japanese', nativeName: '日本語', flag: '🇯🇵', priority: 4 },
  { code: 'ko', name: 'Korean', nativeName: '한국어', flag: '🇰🇷', priority: 4 },
  { code: 'ar', name: 'Arabic', nativeName: 'العربية', flag: '🇸🇦', priority: 4 },
  { code: 'he', name: 'Hebrew', nativeName: 'עברית', flag: '🇮🇱', priority: 4 },

  // Priority 5: Other major world languages
  { code: 'hi', name: 'Hindi', nativeName: 'हिन्दी', flag: '🇮🇳', priority: 5 },
  { code: 'bn', name: 'Bengali', nativeName: 'বাংলা', flag: '🇧🇩', priority: 5 },
  { code: 'fa', name: 'Persian', nativeName: 'فارسی', flag: '🇮🇷', priority: 5 },
  { code: 'id', name: 'Indonesian', nativeName: 'Bahasa Indonesia', flag: '🇮🇩', priority: 5 },
  { code: 'ms', name: 'Malay', nativeName: 'Bahasa Melayu', flag: '🇲🇾', priority: 5 },
  { code: 'th', name: 'Thai', nativeName: 'ไทย', flag: '🇹🇭', priority: 5 },
  { code: 'vi', name: 'Vietnamese', nativeName: 'Tiếng Việt', flag: '🇻🇳', priority: 5 },
  { code: 'tl', name: 'Filipino', nativeName: 'Filipino', flag: '🇵🇭', priority: 5 },
  { code: 'sw', name: 'Swahili', nativeName: 'Kiswahili', flag: '🇰🇪', priority: 5 },
  { code: 'am', name: 'Amharic', nativeName: 'አማርኛ', flag: '🇪🇹', priority: 5 },
  { code: 'ur', name: 'Urdu', nativeName: 'اردو', flag: '🇵🇰', priority: 5 },

  // Priority 6: Other languages
  { code: 'af', name: 'Afrikaans', nativeName: 'Afrikaans', flag: '🇿🇦', priority: 6 },
  { code: 'hy', name: 'Armenian', nativeName: 'Հdelays', flag: '🇦🇲', priority: 6 },
  { code: 'az', name: 'Azerbaijani', nativeName: 'Azərbaycan dili', flag: '🇦🇿', priority: 6 },
  { code: 'my', name: 'Burmese', nativeName: 'မြန်မာ', flag: '🇲🇲', priority: 6 },
  { code: 'ka', name: 'Georgian', nativeName: 'ქართული', flag: '🇬🇪', priority: 6 },
  { code: 'gu', name: 'Gujarati', nativeName: 'ગુજરાતી', flag: '🇮🇳', priority: 6 },
  { code: 'jv', name: 'Javanese', nativeName: 'Basa Jawa', flag: '🇮🇩', priority: 6 },
  { code: 'yo', name: 'Yoruba', nativeName: 'Yorùbá', flag: '🇳🇬', priority: 6 },
  { code: 'kn', name: 'Kannada', nativeName: 'ಕನ್ನಡ', flag: '🇮🇳', priority: 6 },
  { code: 'kk', name: 'Kazakh', nativeName: 'Қазақ тілі', flag: '🇰🇿', priority: 6 },
  { code: 'ky', name: 'Kyrgyz', nativeName: 'Кыргыз тили', flag: '🇰🇬', priority: 6 },
  { code: 'km', name: 'Khmer', nativeName: 'ភាសាខ្មែរ', flag: '🇰🇭', priority: 6 },
  { code: 'ku', name: 'Kurdish', nativeName: 'Kurdî', flag: '🏴󠁫󠁲󠁧󠁿', priority: 6 },
  { code: 'lo', name: 'Lao', nativeName: 'ພາສາລາວ', flag: '🇱🇦', priority: 6 },
  { code: 'ml', name: 'Malayalam', nativeName: 'മലയാളം', flag: '🇮🇳', priority: 6 },
  { code: 'mr', name: 'Marathi', nativeName: 'मराठी', flag: '🇮🇳', priority: 6 },
  { code: 'mn', name: 'Mongolian', nativeName: 'Монгол', flag: '🇲🇳', priority: 6 },
  { code: 'ne', name: 'Nepali', nativeName: 'नेपाली', flag: '🇳🇵', priority: 6 },
  { code: 'pa', name: 'Punjabi', nativeName: 'ਪੰਜਾਬੀ', flag: '🇮🇳', priority: 6 },
  { code: 'si', name: 'Sinhala', nativeName: 'සිංහල', flag: '🇱🇰', priority: 6 },
  { code: 'so', name: 'Somali', nativeName: 'Soomaali', flag: '🇸🇴', priority: 6 },
  { code: 'ta', name: 'Tamil', nativeName: 'தமிழ்', flag: '🇮🇳', priority: 6 },
  { code: 'te', name: 'Telugu', nativeName: 'తెలుగు', flag: '🇮🇳', priority: 6 },
  { code: 'uz', name: 'Uzbek', nativeName: 'Oʻzbek', flag: '🇺🇿', priority: 6 },
  { code: 'zu', name: 'Zulu', nativeName: 'isiZulu', flag: '🇿🇦', priority: 6 },

  // Priority 7: Less common languages
  { code: 'as', name: 'Assamese', nativeName: 'অসমীয়া', flag: '🇮🇳', priority: 7 },
  { code: 'bh', name: 'Bihari', nativeName: 'भोजपुरी', flag: '🇮🇳', priority: 7 },
  { code: 'ceb', name: 'Cebuano', nativeName: 'Cebuano', flag: '🇵🇭', priority: 7 },
  { code: 'dv', name: 'Divehi', nativeName: 'ދިވެހި', flag: '🇲🇻', priority: 7 },
  { code: 'ee', name: 'Ewe', nativeName: 'Eʋegbe', flag: '🇬🇭', priority: 7 },
  { code: 'fj', name: 'Fijian', nativeName: 'Vosa Vakaviti', flag: '🇫🇯', priority: 7 },
  { code: 'ha', name: 'Hausa', nativeName: 'Harshen Hausa', flag: '🇳🇬', priority: 7 },
  { code: 'hmn', name: 'Hmong', nativeName: 'Hmong', flag: '🇱🇦', priority: 7 },
  { code: 'ig', name: 'Igbo', nativeName: 'Asụsụ Igbo', flag: '🇳🇬', priority: 7 },
  { code: 'iu', name: 'Inuktitut', nativeName: 'ᐃᓄᒃᑎᑐᑦ', flag: '🇨🇦', priority: 7 },
  { code: 'kok', name: 'Konkani', nativeName: 'कोंकणी', flag: '🇮🇳', priority: 7 },
  { code: 'co', name: 'Corsican', nativeName: 'Corsu', flag: '🇫🇷', priority: 7 },
  { code: 'ln', name: 'Lingala', nativeName: 'Lingála', flag: '🇨🇩', priority: 7 },
  { code: 'lb', name: 'Luxembourgish', nativeName: 'Lëtzebuergesch', flag: '🇱🇺', priority: 7 },
  { code: 'mi', name: 'Maori', nativeName: 'Te Reo Māori', flag: '🇳🇿', priority: 7 },
  { code: 'or', name: 'Odia', nativeName: 'ଓଡ଼ିଆ', flag: '🇮🇳', priority: 7 },
  { code: 'om', name: 'Oromo', nativeName: 'Afaan Oromoo', flag: '🇪🇹', priority: 7 },
  { code: 'sm', name: 'Samoan', nativeName: 'Gagana Samoa', flag: '🇼🇸', priority: 7 },
  { code: 'su', name: 'Sundanese', nativeName: 'Basa Sunda', flag: '🇮🇩', priority: 7 },
  { code: 'tg', name: 'Tajik', nativeName: 'Тоҷикӣ', flag: '🇹🇯', priority: 7 },
  { code: 'tk', name: 'Turkmen', nativeName: 'Türkmen', flag: '🇹🇲', priority: 7 },
  { code: 'wa', name: 'Walloon', nativeName: 'Walon', flag: '🇧🇪', priority: 7 },
  { code: 'xh', name: 'Xhosa', nativeName: 'isiXhosa', flag: '🇿🇦', priority: 7 },
];

// Type for any supported language code
export type SupportedLanguage = string;

// Localized language names for UI display
// Key = UI language code, Value = map of language code → localized name
const LOCALIZED_LANGUAGE_NAMES: Record<string, Record<string, string>> = {
  hr: {
    en: 'engleski', hr: 'hrvatski', de: 'njemački', it: 'talijanski', sl: 'slovenski',
    cs: 'češki', pl: 'poljski', hu: 'mađarski', sk: 'slovački', fr: 'francuski',
    nl: 'nizozemski', es: 'španjolski', pt: 'portugalski', ru: 'ruski', uk: 'ukrajinski',
    sr: 'srpski', bs: 'bosanski', mk: 'makedonski', bg: 'bugarski', ro: 'rumunjski',
    el: 'grčki', fi: 'finski', sv: 'švedski', no: 'norveški', da: 'danski',
    is: 'islandski', et: 'estonski', lv: 'latvijski', lt: 'litavski', mt: 'malteški',
    ga: 'irski', cy: 'velški', eu: 'baskijski', ca: 'katalonski', gl: 'galicijski',
    be: 'bjeloruski', sq: 'albanski', tr: 'turski',
    zh: 'kineski (pojednostavljeni)', 'zh-TW': 'kineski (tradicionalni)',
    ja: 'japanski', ko: 'korejski', ar: 'arapski', he: 'hebrejski',
    hi: 'hindski', bn: 'bengalski', fa: 'perzijski', id: 'indonezijski',
    ms: 'malajski', th: 'tajski', vi: 'vijetnamski', tl: 'filipinski',
    sw: 'svahili', am: 'amharski', ur: 'urdski',
    af: 'afrikaanski', hy: 'armenski', az: 'azerbajdžanski', ka: 'gruzijski',
    lb: 'luksemburški', co: 'korzički',
  },
  en: {
    en: 'English', hr: 'Croatian', de: 'German', it: 'Italian', sl: 'Slovenian',
    cs: 'Czech', pl: 'Polish', hu: 'Hungarian', sk: 'Slovak', fr: 'French',
    nl: 'Dutch', es: 'Spanish', pt: 'Portuguese', ru: 'Russian', uk: 'Ukrainian',
    sr: 'Serbian', bs: 'Bosnian', mk: 'Macedonian', bg: 'Bulgarian', ro: 'Romanian',
    el: 'Greek', fi: 'Finnish', sv: 'Swedish', no: 'Norwegian', da: 'Danish',
    is: 'Icelandic', et: 'Estonian', lv: 'Latvian', lt: 'Lithuanian', mt: 'Maltese',
    ga: 'Irish', cy: 'Welsh', eu: 'Basque', ca: 'Catalan', gl: 'Galician',
    be: 'Belarusian', sq: 'Albanian', tr: 'Turkish',
    zh: 'Chinese (Simplified)', 'zh-TW': 'Chinese (Traditional)',
    ja: 'Japanese', ko: 'Korean', ar: 'Arabic', he: 'Hebrew',
    hi: 'Hindi', bn: 'Bengali', fa: 'Persian', id: 'Indonesian',
    ms: 'Malay', th: 'Thai', vi: 'Vietnamese', tl: 'Filipino',
    sw: 'Swahili', am: 'Amharic', ur: 'Urdu',
    af: 'Afrikaans', hy: 'Armenian', az: 'Azerbaijani', ka: 'Georgian',
    lb: 'Luxembourgish', co: 'Corsican',
  },
};

/**
 * Get language name localized in the given UI language.
 * Falls back to English name, then native name.
 */
export function getLocalizedLanguageName(langCode: string, uiLanguage: string): string {
  const uiLang = uiLanguage.substring(0, 2);
  // Try localized name
  const localized = LOCALIZED_LANGUAGE_NAMES[uiLang]?.[langCode];
  if (localized) return localized;
  // Fallback to English name
  const englishName = LOCALIZED_LANGUAGE_NAMES['en']?.[langCode];
  if (englishName) return englishName;
  // Fallback to name from WORLD_LANGUAGES
  return WORLD_LANGUAGES.find(l => l.code === langCode)?.name || langCode;
}

// Legacy array for backwards compatibility (simplified format)
export const SUPPORTED_LANGUAGES: { code: string; name: string; flag: string }[] = WORLD_LANGUAGES.map(
  (lang) => ({
    code: lang.code,
    name: lang.nativeName,
    flag: lang.flag,
  })
);

/** Why a translation failed (absent on success). */
export type TranslationFailureReason = 'rate_limited' | 'echo' | 'too_long' | 'network' | 'error';

export interface TranslationResult {
  /** The translation — or, when `success` is false, the original text. */
  translatedText: string;
  detectedLanguage?: string;
  success: boolean;
  error?: string;
  reason?: TranslationFailureReason;
}

// ── Untranslated-echo detection ──────────────────────────────────────────
// Gemini-lite sometimes returns Croatian text unchanged or "rephrased" in
// Croatian instead of translating it. Such a result must not be cached as a
// translation (it would show Croatian to the guest for days). Mirrors the
// check in supabase/functions/translate-content/index.ts.
const CROATIAN_LETTERS = /[čćžšđ]/gi;
const CROATIAN_LIKE = new Set(['hr', 'bs', 'sr']);
/** Languages whose own alphabet has č/š/ž (or đ) — letters prove nothing there. */
const SHARES_CROATIAN_LETTERS = new Set(['hr', 'sr', 'bs', 'sl', 'cs', 'sk', 'pl', 'lt', 'lv', 'et', 'vi']);
/** Croatian function words that are not English words. */
const CROATIAN_WORDS = new Set([
  'je', 'su', 'na', 'za', 'se', 'od', 'ili', 'sa', 'kod', 'nije', 'nisu', 'bez', 'pri', 'ako', 'kada', 'gdje',
  'molimo', 'vas', 'vam', 'smo', 'ste', 'biti', 'mogu', 'nakon', 'prije', 'kroz', 'iz', 'te', 'da', 'ne', 'ima',
  'ali', 'jer', 'koji', 'koja', 'koje', 'samo', 'uz', 'prema', 'oko', 'dok', 'u',
]);

/**
 * č/ć/ž/š/đ in ordinary (lower-case) words only. Names — "Kaštel Štafilić",
 * "Konoba Šime", "Čiovo" — keep their letters in every language, so they must
 * not make a correct translation look like untranslated Croatian.
 */
function commonWordLetters(text: string): number {
  const words = text.match(/\p{L}+/gu) ?? [];
  return words
    // Ordinary words: lower-case, ALL-CAPS ("ZABRANJENO PUŠENJE"), or the first
    // word of a phrase ("Sušilo za kosu"). Capitalised words inside are names.
    .filter((w, i) => w === w.toLowerCase() || (w.length > 1 && w === w.toUpperCase())
      // First word of a phrase that has ordinary words ("Sušilo za kosu"); in an
      // all-capitalised name ("Žuta Lokva", "Šime Ljubića 5") it's part of the name.
      || (i === 0 && words.slice(1).some((x) => x === x.toLowerCase())))
    .reduce((n, w) => n + (w.match(CROATIAN_LETTERS) ?? []).length, 0);
}

/**
 * Croatian function words kept from the source ("je", "za", "molimo"…). Only
 * words present in BOTH count — "da", "se", "ne" are ordinary Italian/Spanish/
 * French words too, so their presence in the output alone proves nothing.
 */
// Short Croatian function words that are ordinary words in the target language
// ("se" in es/it/fr/pt, "da" in it/pt/de, "je" in fr/nl/de…) — prove nothing there.
const ORDINARY_IN_TARGET: Record<string, string[]> = {
  it: ['da', 'se', 'ne', 'te', 'su', 'sa', 'u'],
  es: ['se', 'te', 'su', 'da', 'na'],
  fr: ['je', 'se', 'te', 'ne', 'sa', 'u'],
  pt: ['na', 'se', 'te', 'da'],
  nl: ['je', 'te', 'na', 'da'],
  de: ['da', 'je'],
  en: [],
};

function hasCroatianWords(source: string, output: string, targetLang = ''): boolean {
  const ordinary = new Set(ORDINARY_IN_TARGET[(targetLang || '').split('-')[0]] ?? []);
  const inSource = new Set((source.toLowerCase().match(/\p{L}+/gu) ?? []).filter((w) => CROATIAN_WORDS.has(w) && !ordinary.has(w)));
  return (output.toLowerCase().match(/\p{L}+/gu) ?? []).some((w) => inSource.has(w));
}

function normalizeForCompare(s: string): string {
  return s.toLowerCase().replace(/[\s\p{P}]+/gu, ' ').trim();
}

/** Does the text read as Croatian (letters or several Croatian function words)? */
export function looksCroatian(text: string): boolean {
  if (commonWordLetters(text) > 0) return true;
  const words = text.toLowerCase().match(/\p{L}+/gu) ?? [];
  if (words.length === 0) return false;
  const hits = words.filter((w) => CROATIAN_WORDS.has(w)).length;
  return hits >= 2 && hits / words.length >= 0.1;
}

/**
 * True when `output` is (still) the Croatian source instead of a translation
 * into `targetLang`. Only judged for a Croatian-like source going to another
 * language. An identical result is fine when the source isn't Croatian at all
 * (a host who writes in English, "WiFi", "Netflix").
 */
export function looksUntranslated(source: string, output: string, sourceLang: string, targetLang: string): boolean {
  const src = (sourceLang || '').split('-')[0];
  const tgt = (targetLang || '').split('-')[0];
  if (!CROATIAN_LIKE.has(src) || CROATIAN_LIKE.has(tgt)) return false;
  if (normalizeForCompare(output) === normalizeForCompare(source)) return looksCroatian(source);
  if (SHARES_CROATIAN_LETTERS.has(tgt)) return false;
  const srcCount = commonWordLetters(source);
  const outCount = commonWordLetters(output);
  // Proper nouns ("Čiovo", "Konoba Šime") keep a few letters; an echo keeps most.
  // Kept dish names ("ćevapi, pašticada") aren't an echo — also need Croatian words.
  if (outCount >= 3 && outCount >= srcCount * 0.5 && hasCroatianWords(source, output, tgt)) return true;
  // "Rephrased" Croatian: most of the source's common (lower-case, so not
  // names) words are still there and the output still reads Croatian.
  const common = new Set(
    (source.match(/\p{L}+/gu) ?? []).filter((w) => w.length >= 3 && w === w.toLowerCase()),
  );
  if (common.size < 3) return false;
  const outWords = new Set((output.toLowerCase().match(/\p{L}+/gu) ?? []));
  let kept = 0;
  for (const w of common) if (outWords.has(w)) kept++;
  return kept / common.size >= 0.6 && looksCroatian(output);
}

// Edge Function URL for secure Gemini translations
const TRANSLATE_FUNCTION_URL = `${import.meta.env.VITE_SUPABASE_URL}/functions/v1/translate-content`;

// No third-party fallback (MyMemory was removed 2026-10-23): guests' and hosts'
// private messages go only to our own translate-content function (Gemini).
// When it fails, callers show the original text with a "not translated" mark.

/**
 * Client-side translation cache.
 * Identical (text, source, target) tuples never hit the API twice in the same session.
 * Capped at 200 entries (LRU-ish) so it can't grow unbounded. Only real
 * translations are cached — never a failure or an echo of the source.
 */
const translationCache = new Map<string, TranslationResult>();
const TRANSLATION_CACHE_MAX = 200;

function getCacheKey(text: string, sourceLang: string, targetLang: string): string {
  // Trim + normalize whitespace so trivial edits don't bust the cache.
  return `${sourceLang}|${targetLang}|${text.trim().replace(/\s+/g, ' ')}`;
}

/** Drop one cached translation (e.g. it mangled the link) so the next attempt asks again. */
export function forgetTranslation(text: string, sourceLang: string, targetLang: string): void {
  translationCache.delete(getCacheKey(text, sourceLang, targetLang));
}

/** Forget every translation of this session (e.g. the guest's access ended). */
export function clearTranslationCache(): void {
  translationCache.clear();
}

function cacheTranslation(key: string, result: TranslationResult): void {
  if (translationCache.size >= TRANSLATION_CACHE_MAX) {
    // Evict oldest (insertion order)
    const firstKey = translationCache.keys().next().value;
    if (firstKey) translationCache.delete(firstKey);
  }
  translationCache.set(key, result);
}

/**
 * Is `output` the Croatian source handed back instead of a translation into
 * `targetLang` (looksUntranslated)? Only that counts as an echo: an identical
 * answer for any other source means the text already IS in the target language
 * (a guest who wrote Croatian / English while the app said otherwise, "OK",
 * "WiFi?", names, links) — that is a success, not "not translated".
 */
export function isEchoedTranslation(source: string, output: string, sourceLang: string, targetLang: string): boolean {
  const src = (sourceLang || '').split('-')[0];
  const tgt = (targetLang || '').split('-')[0];
  if (!src || !tgt || src === tgt) return false;
  return looksUntranslated(source, output, src, tgt);
}

/**
 * Translate text using our Edge Function (Gemini; the key stays on the server).
 *
 * Cost protection (in order):
 *   1. Same language → return original (no API call)
 *   2. Empty text → return original
 *   3. In-memory cache hit → return cached translation
 *   4. Edge Function (Gemini) — itself rate-limited per user (60/h) + size-capped
 *
 * A translation is a FAILURE ({ success: false, translatedText: <the original> })
 * when the function errors, reports an error for the field (rate limit, Gemini
 * error, untranslated echo) or hands Croatian back for another language.
 * Callers then show the original, marked "not translated", with a retry.
 */
export async function translateText(
  text: string,
  sourceLang: SupportedLanguage,
  targetLang: SupportedLanguage
): Promise<TranslationResult> {
  // Don't translate if same language
  if (sourceLang === targetLang) {
    return { translatedText: text, success: true };
  }

  // Don't translate empty text
  if (!text.trim()) {
    return { translatedText: text, success: true };
  }

  // Hard cap on input length to avoid accidental megabyte payloads
  const MAX_LEN = 4000;
  if (text.length > MAX_LEN) {
    return {
      translatedText: text,
      success: false,
      reason: 'too_long',
      error: `Text too long (${text.length} chars, max ${MAX_LEN}). Shorten it before translating.`,
    };
  }

  // Cache lookup (saves an Edge Function call on repeated translations of same text)
  const cacheKey = getCacheKey(text, sourceLang, targetLang);
  const cached = translationCache.get(cacheKey);
  if (cached) return cached;

  const result = await translateWithEdgeFunction(text, sourceLang, targetLang);
  if (!result.success) return result;

  if (isEchoedTranslation(text, result.translatedText, sourceLang, targetLang)) {
    return {
      translatedText: text,
      success: false,
      reason: 'echo',
      error: 'Untranslated echo',
    };
  }

  cacheTranslation(cacheKey, result);
  return result;
}

/**
 * Translate using Edge Function (secure - API key on backend)
 */
async function translateWithEdgeFunction(
  text: string,
  sourceLang: SupportedLanguage,
  targetLang: SupportedLanguage
): Promise<TranslationResult> {
  const failure = (reason: TranslationFailureReason, error: string): TranslationResult => ({
    translatedText: text,
    success: false,
    reason,
    error,
  });

  try {
    // Best-effort attach Clerk JWT so the server can rate-limit per-user instead of per-IP.
    // Dynamic import avoids a hard dependency for guest portal callers (no auth there).
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      'Authorization': `Bearer ${import.meta.env.VITE_SUPABASE_ANON_KEY}`,
    };
    try {
      const { getAuthToken, guestAuthHeaders } = await import('@/lib/supabase');
      const token = getAuthToken();
      if (token) headers['x-clerk-token'] = token;
      // Guests (portal chat) identify with the link's token / access code.
      Object.assign(headers, guestAuthHeaders());
    } catch { /* no auth context available */ }

    const response = await fetch(TRANSLATE_FUNCTION_URL, {
      method: 'POST',
      headers,
      body: JSON.stringify({
        fields: [{ name: 'text', value: text }],
        sourceId: 'chat-translation',
        contentType: 'chat',
        sourceLanguage: sourceLang,
        targetLanguage: targetLang,
      }),
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({} as Record<string, unknown>));
      const message = typeof errorData?.error === 'string' ? errorData.error : `Translation failed: ${response.status}`;
      return failure(response.status === 429 ? 'rate_limited' : 'error', message);
    }

    const data = await response.json().catch(() => null) as {
      translations?: Record<string, string>;
      errors?: Record<string, string>;
      rateLimited?: boolean;
    } | null;

    // The function answers 200 even when a field failed — it then puts the
    // ORIGINAL in `translations` and says why in `errors`.
    const fieldError = data?.errors?.text;
    if (fieldError) {
      return failure(/rate limit/i.test(fieldError) ? 'rate_limited' : /echo/i.test(fieldError) ? 'echo' : 'error', fieldError);
    }

    const translatedText = data?.translations?.text;
    if (typeof translatedText === 'string' && translatedText.trim()) {
      return { translatedText, success: true };
    }

    return failure('error', 'No translation in response');
  } catch (error) {
    if (import.meta.env.DEV) console.error('Edge Function translation error:', error);
    return failure('network', error instanceof Error ? error.message : 'Translation failed');
  }
}

/**
 * Get language info by code
 */
export function getLanguageInfo(code: string): LanguageInfo | undefined {
  return WORLD_LANGUAGES.find((lang) => lang.code === code);
}

/**
 * Get sorted languages by priority (and alphabetically within same priority)
 */
export function getSortedLanguages(): LanguageInfo[] {
  return [...WORLD_LANGUAGES].sort((a, b) => {
    if (a.priority !== b.priority) {
      return a.priority - b.priority;
    }
    return a.name.localeCompare(b.name);
  });
}

/**
 * Search languages by name (supports both native name and English name)
 */
export function searchLanguages(query: string): LanguageInfo[] {
  if (!query) return getSortedLanguages();

  const lowerQuery = query.toLowerCase();
  return getSortedLanguages().filter((lang) => {
    const matchesCode = lang.code.toLowerCase().includes(lowerQuery);
    const matchesName = lang.name.toLowerCase().includes(lowerQuery);
    const matchesNativeName = lang.nativeName.toLowerCase().includes(lowerQuery);

    return matchesCode || matchesName || matchesNativeName;
  });
}

/**
 * Get language name by code
 */
export function getLanguageName(code: SupportedLanguage): string {
  return WORLD_LANGUAGES.find((l) => l.code === code)?.name || code;
}

/**
 * Get language native name by code
 */
export function getLanguageNativeName(code: SupportedLanguage): string {
  return WORLD_LANGUAGES.find((l) => l.code === code)?.nativeName || code;
}

/**
 * Get language flag by code
 */
export function getLanguageFlag(code: SupportedLanguage): string {
  return WORLD_LANGUAGES.find((l) => l.code === code)?.flag || '🌐';
}

export default {
  translateText,
  getLanguageInfo,
  getLanguageName,
  getLanguageNativeName,
  getLanguageFlag,
  getSortedLanguages,
  searchLanguages,
  WORLD_LANGUAGES,
  SUPPORTED_LANGUAGES,
};
